import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'

import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { getActionbyAudit } from '../../redux/actions/actionActions'
import {
  getCorrectiveActionStatusForAudit,
  getCorrectiveActionPrioritiesForAudit,
  calculateAuditActionsCosts,
  getAuditbyId,
} from '../../redux/audits/auditActions'
import { analysisColorPalette } from '../../utils/colors'
import PieChart, { hasChartData } from '../../components/common/PieChart'
import {
  AnalysisChartCard,
  AnalysisKpiCard,
  AnalysisPageLoader,
  AnalysisSection,
} from '../../components/common/analysisLayout'
import {
  DetailButton,
  DetailPageHead,
} from '../../components/common/detail'
import {
  AnalysisPdfButton,
  useAnalysisPdfExport,
} from '../../components/common/AnalysisPdfButton'
import AnalysisPdfReport from '../../Pdf/AnalysisPdfReport'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatCurrency, locale } from '../../utils/formatNumbers'
import '../../components/common/maturity-analysis.css'

function AuditAnalysis() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const { totalCost, priorityCount, statusCount } = useSelector(
    (state: RootState) => state.audit
  )
  const audit = useSelector((state: RootState) => state.audit.audit)
  const currency = useSelector(selectSpecifications)[0]?.Currency
  const { id } = useParams<{ id: string }>()
  const pdf = useAnalysisPdfExport()
  const [loading, setLoading] = useState(true)

  React.useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!id) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        await Promise.all([
          dispatch(getAuditbyId(id)),
          dispatch(getCorrectiveActionStatusForAudit(id)),
          dispatch(getCorrectiveActionPrioritiesForAudit(id)),
          dispatch(calculateAuditActionsCosts(id)),
          dispatch(getActionbyAudit(id)),
        ])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [dispatch, id])

  const computedOn = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language]
  )

  const COLORS = analysisColorPalette

  const pieChartDataPriorityCount = {
    labels: priorityCount
      ? Object.keys(priorityCount).map((key) =>
          t(`charts.correctiveActionPriority.labels.${key}`)
        )
      : [],
    datasets: [
      {
        data: priorityCount ? Object.values(priorityCount) : [],
        backgroundColor: COLORS.slice(
          0,
          Object.keys(priorityCount || {}).length
        ),
        hoverBackgroundColor: COLORS.slice(
          0,
          Object.keys(priorityCount || {}).length
        ),
      },
    ],
  }

  const pieChartDataStatusCount = {
    labels: statusCount
      ? Object.keys(statusCount).map((key) =>
          t(`charts.correctiveActionStatus.labels.${key}`)
        )
      : [],
    datasets: [
      {
        data: statusCount ? Object.values(statusCount) : [],
        backgroundColor: COLORS.slice(
          0,
          Object.keys(statusCount || {}).length
        ),
        hoverBackgroundColor: COLORS.slice(
          0,
          Object.keys(statusCount || {}).length
        ),
      },
    ],
  }

  const costValue =
    totalCost === undefined || totalCost === null
      ? '—'
      : formatCurrency(Number(totalCost) || 0, currency || '', {
          locale,
          useIntl: true,
        })

  const backPath = id ? `/audit-detail/${id}` : '/audits'

  return (
    <div className="maturity-analysis-page detail-page">
      <DetailPageHead
        eyebrow={t('analysis.auditAnalysis.eyebrow', {
          defaultValue: 'Assurance · Audits',
        })}
        title={t('analysis.auditAnalysis.pageTitle', {
          defaultValue: 'Audit Analysis',
        })}
        subtitle={t('analysis.auditAnalysis.subtitle', {
          name: audit?.AuditName || '—',
          date: computedOn,
          defaultValue: `Indicators for ${audit?.AuditName || 'this audit'}, computed on ${computedOn}.`,
        })}
        actions={
          <>
            <DetailButton variant="ghost" onClick={() => navigate(backPath)}>
              <ArrowBackOutlined sx={{ fontSize: 16 }} />
              {t('analysis.auditAnalysis.backToAudit', {
                defaultValue: 'Back to audit',
              })}
            </DetailButton>
            <AnalysisPdfButton
              exporting={pdf.exporting}
              disabled={loading}
              onExport={() =>
                pdf.exportPdf(audit?.AuditName || 'audit-analysis')
              }
            />
          </>
        }
      />

      {loading ? (
        <AnalysisPageLoader kpiCount={1} chartCount={2} />
      ) : (
        <>
          <div className="ma-kpi-grid">
            <AnalysisKpiCard
              label={t('charts.totalActionCosts.title')}
              value={costValue}
              tone={Number(totalCost) > 0 ? 'teal' : 'muted'}
              stampTitle={t('analysis.auditAnalysis.computedOn', {
                date: computedOn,
                defaultValue: `Computed on ${computedOn}`,
              })}
              description={t('analysis.auditAnalysis.costDesc', {
                defaultValue:
                  'Total cost of corrective actions linked to this audit.',
              })}
            />
          </div>

          <AnalysisSection
            label={t('analysis.auditAnalysis.sectionActions', {
              defaultValue: 'Corrective actions',
            })}
          >
            <AnalysisChartCard
              title={t('charts.correctiveActionPriority.title')}
              hasData={hasChartData(pieChartDataPriorityCount)}
            >
              <PieChart
                embedded
                title={t('charts.correctiveActionPriority.title')}
                chartTitle={t('charts.correctiveActionPriority.title')}
                data={pieChartDataPriorityCount}
              />
            </AnalysisChartCard>
            <AnalysisChartCard
              title={t('charts.correctiveActionStatus.title')}
              hasData={hasChartData(pieChartDataStatusCount)}
            >
              <PieChart
                embedded
                title={t('charts.correctiveActionStatus.title')}
                chartTitle={t('charts.correctiveActionStatus.title')}
                data={pieChartDataStatusCount}
              />
            </AnalysisChartCard>
          </AnalysisSection>
        </>
      )}

      <div className="ma-pdf-print-host" aria-hidden>
        <AnalysisPdfReport
          ref={pdf.ref}
          eyebrow={t('analysis.auditAnalysis.eyebrow')}
          title={audit?.AuditName || '—'}
          subtitle={t('analysis.auditAnalysis.subtitle', {
            name: audit?.AuditName || '—',
            date: computedOn,
          })}
          footerTitle={t('analysis.auditAnalysis.pageTitle')}
          computedOn={computedOn}
          kpis={[
            {
              label: t('charts.totalActionCosts.title'),
              value: costValue,
              desc: t('analysis.auditAnalysis.costDesc'),
              tone: Number(totalCost) > 0 ? 'teal' : 'muted',
            },
          ]}
          sections={[
            {
              label: t('analysis.auditAnalysis.sectionActions'),
              charts: [
                {
                  key: 'priority',
                  title: t('charts.correctiveActionPriority.title'),
                  kind: 'pie',
                  data: pieChartDataPriorityCount,
                },
                {
                  key: 'status',
                  title: t('charts.correctiveActionStatus.title'),
                  kind: 'pie',
                  data: pieChartDataStatusCount,
                },
              ],
            },
          ]}
        />
      </div>
    </div>
  )
}

export default AuditAnalysis
